import { useEffect, useState, useCallback } from 'react';
import { getBaseUrl } from '../config/apiConfig';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 Minuten
const BUILD_VERSION = process.env.REACT_APP_VERSION || 'dev';

/**
 * Erkennt eine Versions-Drift zwischen dem aktuell geladenen Frontend-Build
 * und dem live Server. Polled regelmäßig `/api/version`. Wenn der Server
 * eine andere Version meldet als die in dieses Bundle eingebackene
 * REACT_APP_VERSION, zeigt einen Banner mit Reload-Button.
 *
 * Robust gegen:
 *   - "dev" build (kein REACT_APP_VERSION gesetzt): keine Warnung
 *   - kurzlebige Netzwerkfehler: einfach ignorieren, nächster Tick prüft erneut
 *   - sichtbarkeitsabhängiges Polling: Tab im Hintergrund → kein Polling-Druck
 */
const AppVersionGuard = () => {
  const [serverVersion, setServerVersion] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const checkVersion = useCallback(async () => {
    try {
      const res = await fetch(`${getBaseUrl()}/api/version`, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'omit',
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.version) setServerVersion(data.version);
    } catch {
      // Netzwerk-Fehler ignorieren — der nächste Tick versucht es wieder.
    }
  }, []);

  useEffect(() => {
    // Initial: einmal direkt prüfen, dann alle 5 min.
    checkVersion();
    const interval = setInterval(() => {
      if (!document.hidden) checkVersion();
    }, CHECK_INTERVAL_MS);

    // Beim Tab-Reaktivieren (z.B. nach längerer Hintergrund-Phase) sofort prüfen
    const onVisibility = () => {
      if (!document.hidden) checkVersion();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [checkVersion]);

  if (
    BUILD_VERSION === 'dev' ||
    !serverVersion ||
    serverVersion === BUILD_VERSION ||
    dismissed
  ) {
    return null;
  }

  const reload = () => {
    // Hard reload — der neue index.html wird vom Server geholt (no-cache),
    // damit der Browser die neuen Hash-benannten JS/CSS-Bundles findet.
    window.location.reload();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] max-w-md w-[calc(100%-2rem)] bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 shadow-xl rounded-2xl px-4 py-3 flex items-center gap-3 animate-in slide-in-from-bottom-4 duration-300"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-tight">
          Neue Version verfügbar
        </p>
        <p className="text-xs opacity-90 leading-tight mt-0.5">
          Lade die Seite neu, um die aktuellste Version zu verwenden.
        </p>
      </div>
      <button
        type="button"
        onClick={reload}
        className="flex-shrink-0 bg-white/20 hover:bg-white/30 dark:bg-slate-900/20 dark:hover:bg-slate-900/30 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-lg transition-colors"
      >
        Neu laden
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Schließen"
        className="flex-shrink-0 text-white/70 hover:text-white dark:text-slate-900/70 dark:hover:text-slate-900 text-lg leading-none -mr-1"
      >
        ×
      </button>
    </div>
  );
};

export default AppVersionGuard;
